import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../shared/axiosInstance';
import { useCompany } from "../../context/CompanyContext";
import { io } from "socket.io-client";

const PurchaseCreditsModal = ({ onClose }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [packageSize, setPackageSize] = useState(1);
    const [discountCode, setDiscountCode] = useState("");
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const { setCompanyCredits } = useCompany();
    const [status, setStatus] = useState("idle"); // idle | processing | success | failed

    useEffect(() => {
        const socket = io(process.env.REACT_APP_API_URL || "http://localhost:5000");
        const storedCompanyId = JSON.parse(localStorage.getItem("user"))?.company;
        const handleCreditUpdate = (data) => {
            if (data.companyId === storedCompanyId.toString()) {
                setCompanyCredits(data.newBalance);
                setStatus("success");
                setMessage("✅ Credits purchased successfully!");
                setProcessing(false);
                onClose();
            }
        };

        socket.on("credit-update", handleCreditUpdate);

        return () => {
            socket.off("credit-update", handleCreditUpdate);
            socket.disconnect();
        };
    }, [onClose, setCompanyCredits]);

    const creditOptions = [
        { size: 1, price: "$250", sub: "" },
        { size: 5, price: "$1000", sub: "($250 savings)" },
        { size: 10, price: "$1750", sub: "($750 savings)" },
    ];

    const initiatePayment = async () => {
        if (!stripe || !elements) return;

        const cardElement = elements.getElement(CardElement);
        if (!cardElement || !cardElement._complete) {
            setMessage("Please fill in your card details.");
            return;
        }

        setProcessing(true);
        setMessage("");

        let res;
        try {
            res = await api.post('/payments/create-intent', {
                purchaseType: "purchaseCredit",
                packageSize,
                discountCode: discountCode.trim(),
            });
        } catch (err) {
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setMessage(serverMsg || "Payment failed. Please try again.");
            setProcessing(false); // stop spinner
            return; // 💥 don't go to Stripe payment if intent failed
        }

        try {
            const { error } = await stripe.confirmCardPayment(res.data.clientSecret, {
                payment_method: { card: cardElement }
            });

            if (error) {
                setMessage(`Payment failed: ${error.message}`);
                console.error("Stripe payment error:", error);
            } else {
                setMessage("Payment successful! Waiting for credit confirmation...");
            }
        } catch (err) {
            console.error("❌ Stripe confirmation error:", err);
            setMessage("Unexpected error during payment.");
        }

        setProcessing(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-bluegray p-6 rounded-lg shadow-lg w-[500px] relative">
                <button onClick={onClose} className="absolute top-2 right-2 text-lightgray text-2xl font-bold">&times;</button>
                <h2 className="text-orange text-xl font-bold mb-4 text-center">Purchase Credits</h2>

                {/* Credit Options */}
                <div className="space-y-3 mb-4">
                    {creditOptions.map(opt => (
                        <div key={opt.size} className={`p-3 border rounded cursor-pointer ${packageSize === opt.size ? 'border-orange bg-darkblue' : 'border-lightblue'}`} onClick={() => setPackageSize(opt.size)}>
                            <p className="text-lightgray font-bold">{opt.size} Credit{opt.size > 1 ? "s" : ""} — {opt.price} {opt.sub && <span className="text-green-400 text-xs">{opt.sub}</span>}</p>
                        </div>
                    ))}
                </div>

                {/* Discount Code Input */}
                <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder="Enter discount code (optional)"
                    className="w-full p-2 mb-4 rounded border border-lightblue bg-deepblue text-lightgray"
                    disabled={processing}
                />

                {/* Stripe card input */}
                <CardElement className="mb-4 p-2 bg-deepblue border border-lightblue rounded text-lightgray" />

                {/* Payment Button */}
                <button
                    onClick={initiatePayment}
                    className="w-full bg-orange hover:bg-deeporange text-darkblue font-bold py-2 rounded"
                    disabled={!stripe || processing}
                >
                    {processing ? "Processing..." : `Pay ${creditOptions.find(o => o.size === packageSize)?.price}`}
                </button>

                {message && <p className="text-sm mt-2 text-red text-center">{message}</p>}

                {processing && (
                    <div className="flex justify-center items-center mb-4">
                        <div className="w-5 h-5 border-2 border-t-2 border-lightblue rounded-full animate-spin"></div>
                        <span className="ml-2 text-lightgray text-sm">Processing payment...</span>
                    </div>
                )}

                {status === "success" && (
                    <button
                        onClick={onClose}
                        className="mt-3 w-full bg-orange hover:bg-deeporange text-darkblue font-bold py-2 rounded"
                    >
                        Close
                    </button>
                )}
            </div>
        </div>
    );
};

export default PurchaseCreditsModal;
